import { beAccountSettimgsApiGetAccountLimits } from 'Api/beAccountSettingsApi'
import { beListApiGetListsLazy } from 'Api/beListApiTS'
import {
  beMessAddBeeTemplateToList,
  beMessApiGenerateBeeCentralizedTemplate,
  beMessApiUpdateBeeTemplateThumbnail,
  beMessCopyBeeTemplateToLists,
  beMessDeleteBeeTemplate,
  beMessGetBeeCentralizedEmailTemplateDetails,
  beMessGetBeeCentralizedEmailTemplates,
  beMessGetBeeTemplateDetails,
  beMessGetBeeTemplates
} from 'Api/BeMessApi'

import {
  evalCondition,
  STORE_KEY_ACCOUNT,
  STORE_KEY_ID_LIST,
  STORE_KEY_USER_LISTS,
  storeGet
} from 'mailup-react-helpers'

import { TEMPLATE_TYPE_BASIC, TEMPLATE_TYPE_LOCAL } from 'Constants/index'

export const mapTemplateItem = (
  idList,
  domain,
  type,
  { Id, Title, Thumbnail }
) => ({
  id: Id,
  title: Title,
  loading: true,
  error: false,
  thumbnail: `//${domain}/${Thumbnail}`,
  retry: 1
})

export async function fetchTemplates({
  type,
  searchText,
  pageNumber,
  pageSize
}) {
  const idList = storeGet(STORE_KEY_ID_LIST)
  const { Domain } = storeGet(STORE_KEY_ACCOUNT)
  const userLists = storeGet(STORE_KEY_USER_LISTS)

  const canEdit = evalCondition('uo:TEMPLATESMANAGEMENT')
  const isOfferLight = evalCondition('c:IsOfferLight')

  const actions = {
    canPreview: true,
    canEdit: type === TEMPLATE_TYPE_LOCAL && canEdit,
    canCopyToLists:
      type === TEMPLATE_TYPE_LOCAL &&
      canEdit &&
      userLists.length > 1 &&
      !isOfferLight,
    canDelete: type === TEMPLATE_TYPE_LOCAL && canEdit,
    canCopy: type !== TEMPLATE_TYPE_LOCAL && canEdit
  }

  const localFilterBy = searchText.trim()
    ? `Title.Contains('${searchText.trim().replace(/'/g, "\\'")}')`
    : ''

  const centralizedFilterBy =
    type === TEMPLATE_TYPE_BASIC ? 'Category == 0' : 'Category == 1'

  const { Items, TotalItems, PageNumber, PageSize } = await (type ===
  TEMPLATE_TYPE_LOCAL
    ? beMessGetBeeTemplates({
        idList,
        filterBy: localFilterBy,
        orderBy: 'Id DESC',
        pageNum: pageNumber,
        pageSize
      })
    : beMessGetBeeCentralizedEmailTemplates({
        filterBy: centralizedFilterBy,
        orderBy: 'Title ASC',
        pageNum: 0,
        pageSize: 0
      }))

  const pageNotExist = !Items.length && TotalItems && PageNumber > 1

  if (pageNotExist) {
    const retryPageNumber = Math.ceil(TotalItems / pageSize)
    const { Items: retryItems } = await beMessGetBeeTemplates({
      idList,
      filterBy: localFilterBy,
      orderBy: 'Id DESC',
      pageNum: retryPageNumber,
      pageSize
    })

    return {
      items: retryItems.map(item =>
        mapTemplateItem(idList, Domain, type, item)
      ),
      totalItems: TotalItems,
      pageNumber: retryPageNumber,
      pageSize: PageSize,
      actions
    }
  }

  return {
    items: Items.map(item => mapTemplateItem(idList, Domain, type, item)),
    totalItems: TotalItems,
    pageNumber: PageNumber,
    pageSize: PageSize,
    actions
  }
}

export async function getTargetLists() {
  const idList = storeGet(STORE_KEY_ID_LIST)
  const { Items } = await beListApiGetListsLazy({
    force: true,
    pageSize: 0,
    pageNumber: 0,
    orderBy: 'Id',
    orderDescending: false
  })

  return Items.filter(({ IdList }) => IdList !== idList).map(
    ({ IdList, Name }) => ({
      value: IdList,
      label: `${IdList} - ${Name}`
    })
  )
}

export const getBeeTemplateDetails = beMessGetBeeTemplateDetails

export async function copyTemplate(id, title, lists) {
  const result = await beMessCopyBeeTemplateToLists({ id, title, lists })

  return result
}

export async function addTemplate(idList, data) {
  const result = await beMessAddBeeTemplateToList(idList, data)
  return result
}

export const deleteTemplate = beMessDeleteBeeTemplate

export async function generateThumbnail(type, idTemplate) {
  const idList = storeGet(STORE_KEY_ID_LIST)
  const { Domain } = storeGet(STORE_KEY_ACCOUNT)

  await (type === TEMPLATE_TYPE_LOCAL
    ? beMessApiUpdateBeeTemplateThumbnail(idList, idTemplate)
    : beMessApiGenerateBeeCentralizedTemplate(idTemplate))

  const { Thumbnail } = await (type === TEMPLATE_TYPE_LOCAL
    ? beMessGetBeeTemplateDetails(idTemplate)
    : beMessGetBeeCentralizedEmailTemplateDetails(idTemplate))

  return `//${Domain}/${Thumbnail}?ts=${Date.now()}`
}

export const checkTemplatesLimitsExceeded = async () => {
  const idList = storeGet(STORE_KEY_ID_LIST)

  const [{ MaxTemplates }, { TotalItems }] = await Promise.all([
    beAccountSettimgsApiGetAccountLimits(),
    beMessGetBeeTemplates({
      idList,
      filterBy: '',
      orderBy: 'Id DESC',
      pageNum: 1,
      pageSize: 1
    })
  ])

  return MaxTemplates > 0 && TotalItems >= MaxTemplates
}
